import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, CheckCircle2 } from 'lucide-react';
import { API, fmtDate, inputCls } from './groupMeta';
import { SEVERITY_HEX } from './RaiseConcernModal';

const STATUS_HEX = { open: '#F43F5E', reviewing: '#F59E0B', resolved: '#10B981' };

const ConcernCard = ({ c, canManage, onUpdate }) => {
  const [res, setRes] = useState(c.resolution || '');
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 space-y-2" data-testid={`concern-${c.id}`} style={{ borderLeft: `3px solid ${SEVERITY_HEX[c.severity]}` }}>
      <div className="flex flex-wrap items-center gap-2 text-[10px]">
        <span className="px-2 py-0.5 rounded-full font-semibold uppercase tracking-[0.1em]" style={{ background: `${SEVERITY_HEX[c.severity]}22`, color: SEVERITY_HEX[c.severity] }}>{c.severity}</span>
        <span className="text-white/70 capitalize">{c.category}</span>
        <span className="text-white/35">raised by {c.raised_by_name || 'you'} · {fmtDate(c.created_at)}</span>
        <div className="flex-1" />
        <span className="px-2 py-0.5 rounded-full font-semibold capitalize" style={{ color: STATUS_HEX[c.status], background: `${STATUS_HEX[c.status]}18` }} data-testid={`concern-status-${c.id}`}>{c.status}</span>
      </div>
      <p className="text-sm text-white/80 whitespace-pre-line">{c.description}</p>
      {canManage && c.status !== 'resolved' ? (
        <div className="flex flex-col md:flex-row gap-2 pt-1">
          <input value={res} onChange={(e) => setRes(e.target.value)} placeholder="Resolution / response" className={inputCls} data-testid={`concern-resolution-${c.id}`} />
          {c.status === 'open' && <button onClick={() => onUpdate(c, 'reviewing', res)} data-testid={`concern-review-${c.id}`} className="px-4 py-2 rounded-xl text-xs border border-amber-400/30 text-amber-200 whitespace-nowrap">Mark reviewing</button>}
          <button onClick={() => onUpdate(c, 'resolved', res)} data-testid={`concern-resolve-${c.id}`} className="inline-flex items-center gap-1 px-4 py-2 rounded-xl text-xs font-semibold text-[#0a0a14] bg-emerald-400 whitespace-nowrap"><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>
        </div>
      ) : c.resolution ? <p className="text-xs text-emerald-200/80 bg-emerald-500/[0.06] rounded-lg px-3 py-2">Resolution: {c.resolution}</p> : null}
    </div>
  );
};

export const ConcernList = ({ concerns, canManage, getAuthHeaders, onChanged }) => {
  const update = async (c, status, resolution) => {
    try { await axios.patch(`${API}/concerns/${c.id}`, { status, resolution }, { headers: getAuthHeaders() }); toast.success(`Concern ${status}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  if (!concerns.length) return <div className="text-center py-12 text-white/40 text-sm"><Flag className="w-7 h-7 mx-auto mb-2 text-white/20" />No concerns raised.</div>;
  return <div className="space-y-2">{concerns.map(c => <ConcernCard key={c.id} c={c} canManage={canManage} onUpdate={update} />)}</div>;
};

export const GroupConcernsTab = ({ group, getAuthHeaders, onChanged, onRaise }) => (
  <div className="space-y-4" data-testid="group-concerns-tab">
    <div className="flex items-center justify-between">
      <p className="text-xs text-white/50">{(group.concerns || []).filter(c => c.status !== 'resolved').length} unresolved</p>
      {group.status !== 'archived' && <button onClick={onRaise} data-testid="concerns-raise-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs border border-amber-400/30 text-amber-200 bg-amber-500/10"><Flag className="w-3.5 h-3.5" /> Raise concern</button>}
    </div>
    <ConcernList concerns={group.concerns || []} canManage={group.can_inspect} getAuthHeaders={getAuthHeaders} onChanged={onChanged} />
  </div>
);
